import { useCallback, useEffect, useMemo, useRef, type ContextType, type PropsWithChildren } from 'react';

import type { IAnalyticsOrderDetailsTab, IAnalyticsReportTab } from '@src/containers/Analytics/Reports/types';
import { isUserRole } from '@src/hooks/useRoleAuth';
import { usePortfolioAppConfigSettings } from '@src/providers/AppConfigProviderHooks/usePortfolioAppConfigSettings';
import { getAppConfigSliceActions } from '@src/providers/AppConfigRedux/AppConfigSlice';
import { selectFullAppConfig } from '@src/providers/AppConfigRedux/selectors';
import { useAppStateDispatch, useAppStateSelector } from '@src/providers/AppStateProvider/AppStateProviderHooks';
import { BlottersContext } from '@src/providers/BlottersContext';
import { OrderPresetsContext, type IOrderPreset } from '@src/providers/OrderPresetsContext';
import { PortfolioSettingsContext } from '@src/providers/PortfolioSettingContext';
import { SoundSettingsContext } from '@src/providers/SoundProvider';
import {
  ContextGuideContext,
  DefaultFavoriteSecuritiesConfig,
  DefaultFavoriteSecuritiesMethods,
  DefaultFavoriteStrategiesConfig,
  DefaultFavoriteStrategiesMethods,
  DefaultFavoriteSubAccountsConfigState,
  DefaultFavoriteSubAccountsMethods,
  DefaultMarketAccountsConfigState,
  DefaultMarketAccountsContext,
  DefaultMarketAccountsMethods,
  FavoriteSecuritiesContext,
  FavoriteStrategiesContext,
  FavoriteSubAccountsContext,
  ROLE,
  RecentSymbolsContext,
  TabsStorage,
  getTypedKeys,
  useConstant,
  type TabsState,
  type UnifiedLiquidityEnum,
  type User,
} from '@talos/kyoko';
import { get, isEqual, keys, pick } from 'lodash-es';
import { useLocation } from 'react-router-dom';
import { useMethods } from 'react-use';
import { BehaviorSubject } from 'rxjs';
import {
  MarketSelectorConfigContext,
  type MarketSelectorConfigContextProps,
} from '../../components/MarketSelector/MarketSelectorConfigContext';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { getPinSection } from '../../utils/getPinSection';
import type { AppLayoutConfigContextProps } from '../AppLayoutConfig/AppLayoutConfigContext';
import { AppLayoutConfigContext } from '../AppLayoutConfig/AppLayoutConfigContext';
import type { SpaceConfigContextProps } from '../AppLayoutConfig/SpaceConfigContext';
import { SpaceConfigContext } from '../AppLayoutConfig/SpaceConfigContext';
import {
  DefaultAggregationConfig,
  DefaultAggregationContext,
  DefaultAggregationMethods,
} from '../DefaultAggregationContext';
import { DisplaySettingsContext } from '../DisplaySettingsProvider';
import {
  DefaultFavoriteAggregationsConfig,
  DefaultFavoriteAggregationsMethods,
  FavoriteAggregationsContext,
} from '../FavoriteAggregationsContext';
import { OrderEntryContext } from '../OrderEntryContext';
import { TradingSettingsContext } from '../TradingSettingsContext';
import {
  ANALYTICS_REPORTS_TABS_ID,
  AnalyticsTabsContext,
  useAnalyticsTabsContext,
  type UseAnalyticsTabsContextProps,
} from '../useAnalyticsTabs';
import {
  MonitoringTabsContext,
  useMonitoringTabsContext,
  type MonitoringTab,
  type UseMonitoringTabsContextProps,
} from '../useMonitoringTabs';
import type { AppConfigState } from './AppConfigProvider.types';
import { createMethods } from './createMethods';

export { useBlotterState } from '../BlottersContext';
export { useDisplaySettings } from '../DisplaySettingsProvider';
export { useOrderEntry } from '../OrderEntryContext';
export { useTradingSettings } from '../TradingSettingsContext';

export interface IDDHOrderDefault {
  id: string;
  symbol?: string;
  strategy?: string;
  aggregation?: string;
  unifiedLiquidity?: UnifiedLiquidityEnum;
}

export type AppConfigStateMethods = ReturnType<typeof createMethods>;
export const AppConfigProvider = function AppConfigProvider({ user, children }: PropsWithChildren<{ user: User }>) {
  const location = useLocation();
  const dispatch = useAppStateDispatch();
  const reduxState = useAppStateSelector(selectFullAppConfig);
  const initialState = useConstant(reduxState);
  const { setConfig } = getAppConfigSliceActions();
  const [state, methods]: [AppConfigState, AppConfigStateMethods] = useMethods<typeof createMethods, AppConfigState>(
    createMethods as any,
    initialState
  ) as [AppConfigState, AppConfigStateMethods];

  const appConfigSubject = useConstant(new BehaviorSubject(initialState));
  useEffect(() => {
    if (state && !isEqual(appConfigSubject.value, state)) {
      dispatch(setConfig(state));
      appConfigSubject.next(state);
    }
  }, [appConfigSubject, dispatch, setConfig, state]);

  const enableAdvancedOrderDetails = useMemo((): boolean => {
    // Initial state depends on user roles
    // https://talostrading.atlassian.net/browse/UI-3969
    if (state?.enableAdvancedOrderDetails === undefined) {
      return [ROLE.TALOS_ADMIN, ROLE.ADMIN, ROLE.TALOS_SUPPORT, ROLE.TALOS_VIEWER].some(role => isUserRole(user, role));
    } else {
      return state.enableAdvancedOrderDetails;
    }
  }, [user, state]);

  const { allowOldMarketSelector, enableDelta1BPSPricingMode } = useFeatureFlag();

  const recentSymbols = useMemo<ContextType<typeof RecentSymbolsContext>>(
    () => ({
      recentSymbols: state.recentSymbols,
      setRecentSymbols: methods.setRecentSymbols,
    }),
    [state.recentSymbols, methods.setRecentSymbols]
  );

  const pinNavigationSidebarSection = state.pinNavigationSidebarSection;
  const setPinNavigationSidebarSection = methods.setPinNavigationSidebarSection;
  const display = useMemo<ContextType<typeof DisplaySettingsContext>>(
    () => ({
      termsAccepted: state.termsAccepted,
      showAllInPrices: state.showAllInPrices,
      showFirmLiquidity: state.showFirmLiquidity,
      // Gate showDelta1BPS by org feature flag - when disabled, BPS mode is forced off
      showDelta1BPS: enableDelta1BPSPricingMode && state.showDelta1BPS,
      showTotalEstimates: state.showTotalEstimates,
      homeCurrency: state.homeCurrency,
      customEOD: state.customEOD,
      // Fallback to legacy pinNavigationSidebar if section is not set in pinNavigationSidebarSection
      pinNavigationSidebar: pinNavigationSidebarSection[getPinSection(location.pathname)] ?? state.pinNavigationSidebar,
      pinNavigationSidebarSection,
      latestReleaseNotesRead: state.latestReleaseNotesRead,
      otcSettlementShowAll: state.otcSettlementShowAll,
      showSidebarLabels: state.showSidebarLabels,
      shouldConfirmCloseTab: state.shouldConfirmCloseTab,
      adminCounterpartiesShowInactiveAccounts: state.adminCounterpartiesShowInactiveAccounts ?? true,
      showAppwideDrawerGuide: state.showAppwideDrawerGuide,
      firstSeenVersion: state.firstSeenVersion,
      seenTours: state.seenTours,
      seenShowcases: state.seenShowcases,
      setTermsAccepted: methods.setTermsAccepted,
      setShowAllInPrices: methods.setShowAllInPrices,
      setShowFirmLiquidity: methods.setShowFirmLiquidity,
      setShowDelta1BPS: methods.setShowDelta1BPS,
      setShowTotalEstimates: methods.setShowTotalEstimates,
      setHomeCurrency: methods.setHomeCurrency,
      setCustomEOD: methods.setCustomEOD,
      setPinNavigationSidebar: (pinNavigationSidebar: boolean) =>
        setPinNavigationSidebarSection(pinNavigationSidebar, getPinSection(location.pathname)),
      setPinNavigationSidebarSection,
      setPinAllNavigationSidebarSections: (pinNavigationSidebar: boolean) => {
        const sections = getTypedKeys(pinNavigationSidebarSection);
        sections.forEach(section => {
          setPinNavigationSidebarSection(pinNavigationSidebar, section);
        });
      },
      setLatestReleaseNotesRead: methods.setLatestReleaseNotesRead,
      setOTCSettlementShowAll: methods.setOTCSettlementShowAll,
      setShowSidebarLabels: methods.setShowSidebarLabels,
      setShouldConfirmCloseTab: methods.setShouldConfirmCloseTab,
      setAdminCounterpartiesShowInactiveAccounts: methods.setAdminCounterpartiesShowInactiveAccounts,
      setShowAppwideDrawerGuide: methods.setShowAppwideDrawerGuide,
      setSeenTours: methods.setSeenTours,
      setSeenShowcases: methods.setSeenShowcases,
    }),
    [
      state.termsAccepted,
      state.showAllInPrices,
      state.showFirmLiquidity,
      state.showDelta1BPS,
      enableDelta1BPSPricingMode,
      state.showTotalEstimates,
      state.homeCurrency,
      state.customEOD,
      state.pinNavigationSidebar,
      pinNavigationSidebarSection,
      state.latestReleaseNotesRead,
      state.otcSettlementShowAll,
      state.showSidebarLabels,
      state.shouldConfirmCloseTab,
      state.adminCounterpartiesShowInactiveAccounts,
      state.showAppwideDrawerGuide,
      state.firstSeenVersion,
      state.seenTours,
      state.seenShowcases,
      methods.setTermsAccepted,
      methods.setShowAllInPrices,
      methods.setShowFirmLiquidity,
      methods.setShowDelta1BPS,
      methods.setShowTotalEstimates,
      methods.setHomeCurrency,
      methods.setCustomEOD,
      setPinNavigationSidebarSection,
      methods.setLatestReleaseNotesRead,
      methods.setOTCSettlementShowAll,
      methods.setShowSidebarLabels,
      methods.setShouldConfirmCloseTab,
      methods.setAdminCounterpartiesShowInactiveAccounts,
      methods.setShowAppwideDrawerGuide,
      methods.setSeenTours,
      methods.setSeenShowcases,
      location.pathname,
    ]
  );

  const trading = useMemo<ContextType<typeof TradingSettingsContext>>(
    () => ({
      allowSyntheticCcy: state.allowSyntheticCcy,
      setAllowSyntheticCcy: methods.setAllowSyntheticCcy,
      allowedSlippage: state.allowedSlippage,
      setAllowedSlippage: methods.setAllowedSlippage,
      defaultSubAccountId: state.defaultSubAccountId,
      setDefaultSubAccountId: methods.setDefaultSubAccountId,
      enableGroups: state.enableGroups,
      confirmOrderCancels: state.confirmOrderCancels,
      confirmOrderResume: state.confirmOrderResume,
      allowSyntheticCrosses: state.allowSyntheticCrosses,
      setEnableGroups: methods.setEnableGroups,
      rememberSubAccountId: state.rememberSubAccountId,
      setRememberSubAccountId: methods.setRememberSubAccountId,
      useTradeAllocations: state.useTradeAllocations,
      setUseTradeAllocations: methods.setUseTradeAllocations,
      exportDatesRaw: state.exportDatesRaw,
      setExportDatesRaw: methods.setExportDatesRaw,
      clickToTradeAll: state.clickToTradeAll,
      setClickToTradeAll: methods.setClickToTradeAll,
      clickToTradeDefaultSubaccount: state.clickToTradeDefaultSubaccount,
      setClickToTradeDefaultSubaccount: methods.setClickToTradeDefaultSubaccount,
      setAllowSyntheticCrosses: methods.setAllowSyntheticCrosses,
      setConfirmOrderCancels: methods.setConfirmOrderCancels,
      setConfirmOrderResume: methods.setConfirmOrderResume,
      enableCustomerPricing: state.enableCustomerPricing,
      setEnableCustomerPricing: methods.setEnableCustomerPricing,
      enableSalesWorkflow: state.enableSalesWorkflow,
      setEnableSalesWorkflow: methods.setEnableSalesWorkflow,
      linkCustomerHedgeSubaccount: state.linkCustomerHedgeSubaccount,
      setLinkCustomerHedgeSubaccount: methods.setLinkCustomerHedgeSubaccount,
      enableDerivativeContractDefault: state.enableDerivativeContractDefault,
      exclusivelyPrimePriceOnRePriming: state.exclusivelyPrimePriceOnRePriming,
      setEnableDerivativeContractDefault: methods.setEnableDerivativeContractDefault,
      setExclusivelyPrimePriceOnRePriming: methods.setExclusivelyPrimePriceOnRePriming,
      enableCustomerBlotterColumns: state.enableCustomerBlotterColumns,
      setEnableCustomerBlotterColumns: methods.setEnableCustomerBlotterColumns,
      customerPricingInitialExpanded: state.customerPricingInitialExpanded,
      setCustomerPricingInitialExpanded: methods.setCustomerPricingInitialExpanded,
      applyCustomerPricingDetailsToOrderComment: state.applyCustomerPricingDetailsToOrderComment,
      setApplyCustomerPricingDetailsToOrderComment: methods.setApplyCustomerPricingDetailsToOrderComment,
      selectedCustomerID: state.selectedCustomerID,
      setSelectedCustomerID: methods.setSelectedCustomerID,
      alwaysCheckPriceReasonability: state.alwaysCheckPriceReasonability,
      setAlwaysCheckPriceReasonability: methods.setAlwaysCheckPriceReasonability,
      enableCustomerTradeBookingOnCPC: state.enableCustomerTradeBookingOnCPC,
      setEnableCustomerTradeBookingOnCPC: methods.setEnableCustomerTradeBookingOnCPC,
      defaultOrderStrategy: state.defaultOrderStrategy,
      setDefaultOrderStrategy: methods.setDefaultOrderStrategy,
      defaultMultilegOrderStrategy: state.defaultMultilegOrderStrategy,
      setDefaultMultilegOrderStrategy: methods.setDefaultMultilegOrderStrategy,
      defaultOrderFormTab: state.defaultOrderFormTab,
      setDefaultOrderFormTab: methods.setDefaultOrderFormTab,
      defaultPriceOffsets: state.defaultPriceOffsets,
      setDefaultPriceOffsets: methods.setDefaultPriceOffsets,
      alwaysUseBBOForPriceOffsets: state.alwaysUseBBOForPriceOffsets,
      setAlwaysUseBBOForPriceOffsets: methods.setAlwaysUseBBOForPriceOffsets,
      startTimeEntryMode: state.startTimeEntryMode,
      setStartTimeEntryMode: methods.setStartTimeEntryMode,
      endTimeEntryMode: state.endTimeEntryMode,
      setEndTimeEntryMode: methods.setEndTimeEntryMode,
      enableAdvancedOrderDetails,
      setEnableAdvancedOrderDetails: methods.setEnableAdvancedOrderDetails,
      alwaysShowBestBidOffer: state.alwaysShowBestBidOffer,
      setAlwaysShowBestBidOffer: methods.setAlwaysShowBestBidOffer,
      alwaysShowMarketCardBestBidOffer: state.alwaysShowMarketCardBestBidOffer,
      setAlwaysShowMarketCardBestBidOffer: methods.setAlwaysShowMarketCardBestBidOffer,
      showOrderFormRollupSelector: state.showOrderFormRollupSelector,
      setShowOrderFormRollupSelector: methods.setShowOrderFormRollupSelector,
      useNewMarketSelector: allowOldMarketSelector ? state.useNewMarketSelector : true,
      setUseNewMarketSelector: methods.setUseNewMarketSelector,
      bulkClosePositionQuoteCurrencies: state.bulkClosePositionQuoteCurrencies,
      setBulkClosePositionQuoteCurrencies: methods.setBulkClosePositionQuoteCurrencies,
      enableReduceFirst: state.enableReduceFirst,
      setEnableReduceFirst: methods.setEnableReduceFirst,
      enableReduceOnly: state.enableReduceOnly,
      setEnableReduceOnly: methods.setEnableReduceOnly,
      priceOffsetBPSBehaviour: state.priceOffsetBPSBehaviour,
      setPriceOffsetBPSBehaviour: methods.setPriceOffsetBPSBehaviour,
      defaultToEmptyOrderMarkets: state.defaultToEmptyOrderMarkets,
      setDefaultToEmptyOrderMarkets: methods.setDefaultToEmptyOrderMarkets,
      resetOrderAccountsOnSubAccountChange: state.resetOrderAccountsOnSubAccountChange,
      setResetOrderAccountsOnSubAccountChange: methods.setResetOrderAccountsOnSubAccountChange,
      omsPopoutGroups: state.omsPopoutGroups,
      setOmsPopoutGroups: methods.setOmsPopoutGroups,
      enableDefaultMarketCardSubAccount: state.enableDefaultMarketCardSubAccount,
      setEnableDefaultMarketCardSubAccount: methods.setEnableDefaultMarketCardSubAccount,
      defaultNewMarketCardsCompact: state.defaultNewMarketCardsCompact,
      setDefaultNewMarketCardsCompact: methods.setDefaultNewMarketCardsCompact,
      newMultilegDefaultDelta1Behaviour: state.newMultilegDefaultDelta1Behaviour,
      setNewMultilegDefaultDelta1Behaviour: methods.setNewMultilegDefaultDelta1Behaviour,
      keepFormOpenAfterSubmit: state.keepFormOpenAfterSubmit,
      setKeepFormOpenAfterSubmit: methods.setKeepFormOpenAfterSubmit,
      confirmOrdersOnDesktop: state.confirmOrdersOnDesktop,
      setConfirmOrdersOnDesktop: methods.setConfirmOrdersOnDesktop,
      confirmOrdersOnMobile: state.confirmOrdersOnMobile,
      setConfirmOrdersOnMobile: methods.setConfirmOrdersOnMobile,
      defaultNewOrderToCounterCurrency: state.defaultNewOrderToCounterCurrency,
      setDefaultNewOrderToCounterCurrency: methods.setDefaultNewOrderToCounterCurrency,
      showAllDecimalsInBlotters: state.showAllDecimalsInBlotters,
      setShowAllDecimalsInBlotters: methods.setShowAllDecimalsInBlotters,
      compactMobileCards: state.compactMobileCards,
      setCompactMobileCards: methods.setCompactMobileCards,
      showMobileQuickSearch: state.showMobileQuickSearch,
      setShowMobileQuickSearch: methods.setShowMobileQuickSearch,
      rfqPricingPanelShowAllDecimals: state.rfqPricingPanelShowAllDecimals,
      setRfqPricingPanelShowAllDecimals: methods.setRfqPricingPanelShowAllDecimals,
      takerQuoteBreakdownShowAllDecimals: state.takerQuoteBreakdownShowAllDecimals,
      setTakerQuoteBreakdownShowAllDecimals: methods.setTakerQuoteBreakdownShowAllDecimals,
      careOrderNettingPreviewShowAllDecimals: state.careOrderNettingPreviewShowAllDecimals,
      setCareOrderNettingPreviewShowAllDecimals: methods.setCareOrderNettingPreviewShowAllDecimals,
      rfqSweepingShowAllDecimals: state.rfqSweepingShowAllDecimals,
      setRfqSweepingShowAllDecimals: methods.setRfqSweepingShowAllDecimals,
      rfqFormTradingProtocol: state.rfqFormTradingProtocol,
      setRfqFormTradingProtocol: methods.setRfqFormTradingProtocol,
      rfqFormDefaultToSweeping: state.rfqFormDefaultToSweeping,
      setRfqFormDefaultToSweeping: methods.setRfqFormDefaultToSweeping,
      rfqFormDefaultToSizeCountering: state.rfqFormDefaultToSizeCountering,
      setRfqFormDefaultToSizeCountering: methods.setRfqFormDefaultToSizeCountering,
      rfqBestQuoteBy: state.rfqBestQuoteBy,
      setRfqBestQuoteBy: methods.setRfqBestQuoteBy,
    }),
    [
      state.allowSyntheticCcy,
      state.allowedSlippage,
      state.defaultSubAccountId,
      state.enableGroups,
      state.confirmOrderCancels,
      state.confirmOrderResume,
      state.allowSyntheticCrosses,
      state.rememberSubAccountId,
      state.useTradeAllocations,
      state.exportDatesRaw,
      state.clickToTradeAll,
      state.clickToTradeDefaultSubaccount,
      state.enableCustomerPricing,
      state.enableSalesWorkflow,
      state.linkCustomerHedgeSubaccount,
      state.enableDerivativeContractDefault,
      state.exclusivelyPrimePriceOnRePriming,
      state.enableCustomerBlotterColumns,
      state.customerPricingInitialExpanded,
      state.applyCustomerPricingDetailsToOrderComment,
      state.selectedCustomerID,
      state.alwaysCheckPriceReasonability,
      state.enableCustomerTradeBookingOnCPC,
      state.defaultOrderStrategy,
      state.defaultMultilegOrderStrategy,
      state.defaultOrderFormTab,
      state.defaultPriceOffsets,
      state.alwaysUseBBOForPriceOffsets,
      state.startTimeEntryMode,
      state.endTimeEntryMode,
      state.alwaysShowBestBidOffer,
      state.alwaysShowMarketCardBestBidOffer,
      state.showOrderFormRollupSelector,
      state.useNewMarketSelector,
      state.bulkClosePositionQuoteCurrencies,
      state.enableReduceFirst,
      state.enableReduceOnly,
      state.priceOffsetBPSBehaviour,
      state.defaultToEmptyOrderMarkets,
      state.resetOrderAccountsOnSubAccountChange,
      state.omsPopoutGroups,
      state.enableDefaultMarketCardSubAccount,
      state.defaultNewMarketCardsCompact,
      state.newMultilegDefaultDelta1Behaviour,
      state.keepFormOpenAfterSubmit,
      state.confirmOrdersOnDesktop,
      state.confirmOrdersOnMobile,
      state.defaultNewOrderToCounterCurrency,
      state.showAllDecimalsInBlotters,
      state.compactMobileCards,
      state.showMobileQuickSearch,
      state.rfqPricingPanelShowAllDecimals,
      state.takerQuoteBreakdownShowAllDecimals,
      state.careOrderNettingPreviewShowAllDecimals,
      state.rfqSweepingShowAllDecimals,
      state.rfqFormTradingProtocol,
      state.rfqFormDefaultToSweeping,
      state.rfqFormDefaultToSizeCountering,
      state.rfqBestQuoteBy,
      allowOldMarketSelector,
      enableAdvancedOrderDetails,
      methods.setAllowSyntheticCcy,
      methods.setAllowedSlippage,
      methods.setDefaultSubAccountId,
      methods.setEnableGroups,
      methods.setRememberSubAccountId,
      methods.setUseTradeAllocations,
      methods.setExportDatesRaw,
      methods.setClickToTradeAll,
      methods.setClickToTradeDefaultSubaccount,
      methods.setAllowSyntheticCrosses,
      methods.setConfirmOrderCancels,
      methods.setConfirmOrderResume,
      methods.setEnableCustomerPricing,
      methods.setLinkCustomerHedgeSubaccount,
      methods.setEnableSalesWorkflow,
      methods.setEnableDerivativeContractDefault,
      methods.setExclusivelyPrimePriceOnRePriming,
      methods.setEnableCustomerBlotterColumns,
      methods.setCustomerPricingInitialExpanded,
      methods.setApplyCustomerPricingDetailsToOrderComment,
      methods.setSelectedCustomerID,
      methods.setAlwaysCheckPriceReasonability,
      methods.setEnableCustomerTradeBookingOnCPC,
      methods.setDefaultOrderStrategy,
      methods.setDefaultMultilegOrderStrategy,
      methods.setDefaultOrderFormTab,
      methods.setDefaultPriceOffsets,
      methods.setAlwaysUseBBOForPriceOffsets,
      methods.setStartTimeEntryMode,
      methods.setEndTimeEntryMode,
      methods.setEnableAdvancedOrderDetails,
      methods.setAlwaysShowBestBidOffer,
      methods.setAlwaysShowMarketCardBestBidOffer,
      methods.setShowOrderFormRollupSelector,
      methods.setUseNewMarketSelector,
      methods.setBulkClosePositionQuoteCurrencies,
      methods.setEnableReduceFirst,
      methods.setEnableReduceOnly,
      methods.setPriceOffsetBPSBehaviour,
      methods.setDefaultToEmptyOrderMarkets,
      methods.setResetOrderAccountsOnSubAccountChange,
      methods.setOmsPopoutGroups,
      methods.setEnableDefaultMarketCardSubAccount,
      methods.setDefaultNewMarketCardsCompact,
      methods.setNewMultilegDefaultDelta1Behaviour,
      methods.setKeepFormOpenAfterSubmit,
      methods.setConfirmOrdersOnDesktop,
      methods.setConfirmOrdersOnMobile,
      methods.setDefaultNewOrderToCounterCurrency,
      methods.setShowAllDecimalsInBlotters,
      methods.setCompactMobileCards,
      methods.setShowMobileQuickSearch,
      methods.setRfqPricingPanelShowAllDecimals,
      methods.setTakerQuoteBreakdownShowAllDecimals,
      methods.setCareOrderNettingPreviewShowAllDecimals,
      methods.setRfqSweepingShowAllDecimals,
      methods.setRfqFormTradingProtocol,
      methods.setRfqFormDefaultToSweeping,
      methods.setRfqFormDefaultToSizeCountering,
      methods.setRfqBestQuoteBy,
    ]
  );

  const blotters = useMemo<ContextType<typeof BlottersContext>>(
    () => ({
      groupMarketAccounts: state.groupMarketAccounts,
      setGroupMarketAccounts: methods.setGroupMarketAccounts,
      balancesHiddenSymbols: state.balancesHiddenSymbols,
      setBalancesHiddenSymbols: methods.setBalancesHiddenSymbols,
      balancesHiddenMarketAccountIDs: state.balancesHiddenMarketAccountIDs,
      setBalancesHiddenMarketAccountIDs: methods.setBalancesHiddenMarketAccountIDs,
      balancesMarketAccountIDsOrder: state.balancesMarketAccountIDsOrder,
      setBalancesMarketAccountIDsOrder: methods.setBalancesMarketAccountIDsOrder,
      balancesCurrenciesOrder: state.balancesCurrenciesOrder,
      setBalancesCurrenciesOrder: methods.setBalancesCurrenciesOrder,
      filterValueCustomerUsers: state.filterValueCustomerUsers,
      enableMonitoringCustomerOrderSummaryColumns: state.enableMonitoringCustomerOrderSummaryColumns,
      setEnableMonitoringCustomerOrderSummaryColumns: methods.setEnableMonitoringCustomerOrderSummaryColumns,
      setFilterValueCustomerUsers: methods.setFilterValueCustomerUsers,
      filterValueAggregations: state.filterValueAggregations,
      setFilterValueAggregations: methods.setFilterValueAggregations,
      filterValueSecurityMaster: state.filterValueSecurityMaster,
      setFilterValueSecurityMaster: methods.setFilterValueSecurityMaster,
      filterValueCustomerAddresses: state.filterValueCustomerAddresses,
      setFilterValueCustomerAddresses: methods.setFilterValueCustomerAddresses,
      filterValueCustomerBalances: state.filterValueCustomerBalances,
      setFilterValueCustomerBalances: methods.setFilterValueCustomerBalances,
      filterValueCustomerTradingLimits: state.filterValueCustomerTradingLimits,
      setFilterValueCustomerTradingLimits: methods.setFilterValueCustomerTradingLimits,
      filterValueCustomerCredit: state.filterValueCustomerCredit,
      setFilterValueCustomerCredit: methods.setFilterValueCustomerCredit,
      filterValueSubAccountTradingLimits: state.filterValueSubAccountTradingLimits,
      setFilterValueSubAccountTradingLimits: methods.setFilterValueSubAccountTradingLimits,
      filterValueSubAccountWindowLimits: state.filterValueSubAccountWindowLimits,
      setFilterValueSubAccountWindowLimits: methods.setFilterValueSubAccountWindowLimits,
      isExpanded: state.isExpanded,
      setIsExpanded: methods.setIsExpanded,
      isMinimized: state.isMinimized,
      setIsMinimized: methods.setIsMinimized,
      sessionBlotterHeight: state.sessionBlotterHeight,
      setSessionBlotterHeight: methods.setSessionBlotterHeight,
      customerBalancesShowZeroBalances: state.customerBalancesShowZeroBalances,
      setCustomerBalancesShowZeroBalances: methods.setCustomerBalancesShowZeroBalances,
      filterValueCustomerMarketAccounts: state.filterValueCustomerMarketAccounts,
      setFilterValueCustomerMarketAccounts: methods.setFilterValueCustomerMarketAccounts,
    }),
    [
      state.groupMarketAccounts,
      state.balancesHiddenSymbols,
      state.balancesHiddenMarketAccountIDs,
      state.balancesMarketAccountIDsOrder,
      state.balancesCurrenciesOrder,
      state.filterValueCustomerUsers,
      state.enableMonitoringCustomerOrderSummaryColumns,
      state.filterValueAggregations,
      state.filterValueSecurityMaster,
      state.filterValueCustomerAddresses,
      state.filterValueCustomerBalances,
      state.filterValueCustomerTradingLimits,
      state.filterValueCustomerCredit,
      state.filterValueSubAccountTradingLimits,
      state.filterValueSubAccountWindowLimits,
      state.isExpanded,
      state.isMinimized,
      state.sessionBlotterHeight,
      state.customerBalancesShowZeroBalances,
      state.filterValueCustomerMarketAccounts,
      methods.setGroupMarketAccounts,
      methods.setBalancesHiddenSymbols,
      methods.setBalancesHiddenMarketAccountIDs,
      methods.setBalancesMarketAccountIDsOrder,
      methods.setBalancesCurrenciesOrder,
      methods.setEnableMonitoringCustomerOrderSummaryColumns,
      methods.setFilterValueCustomerUsers,
      methods.setFilterValueAggregations,
      methods.setFilterValueSecurityMaster,
      methods.setFilterValueCustomerAddresses,
      methods.setFilterValueCustomerBalances,
      methods.setFilterValueCustomerTradingLimits,
      methods.setFilterValueCustomerCredit,
      methods.setFilterValueSubAccountTradingLimits,
      methods.setFilterValueSubAccountWindowLimits,
      methods.setIsExpanded,
      methods.setIsMinimized,
      methods.setSessionBlotterHeight,
      methods.setCustomerBalancesShowZeroBalances,
      methods.setFilterValueCustomerMarketAccounts,
    ]
  );

  const orderEntry = useMemo<ContextType<typeof OrderEntryContext>>(
    () => ({
      lastStrategyUsed: state.lastStrategyUsed,
      setLastStrategyUsed: methods.setLastStrategyUsed,
      lastOrderFormUsed: state.lastOrderFormUsed,
      setLastOrderFormUsed: methods.setLastOrderFormUsed,
      lastProductGroupUsed: state.lastProductGroupUsed,
      setLastProductGroupUsed: methods.setLastProductGroupUsed,
      ddhDefaults: state.ddhDefaults,
      ddhDefaultsByID: new Map(state.ddhDefaults.map(item => [item.id, item])),
      saveDDHDefaults: (ddhDefault: IDDHOrderDefault) => {
        const ddhDefaults = state.ddhDefaults ? [...state.ddhDefaults] : [];
        const foundIndex = ddhDefaults.findIndex(item => item.id === ddhDefault.id);
        if (foundIndex > -1) {
          ddhDefaults[foundIndex] = ddhDefault;
        } else {
          ddhDefaults.push(ddhDefault);
        }
        methods.setDDHDefaults(ddhDefaults);
      },
    }),
    [state.lastStrategyUsed, state.lastOrderFormUsed, state.lastProductGroupUsed, state.ddhDefaults, methods]
  );

  const sounds = useMemo<ContextType<typeof SoundSettingsContext>>(
    () => ({
      enableSoundEffects: state.enableSoundEffects,
      enabledSounds: state.enabledSounds,
      setEnableSoundEffects: methods.setEnableSoundEffects,
      setEnabledSounds: methods.setEnabledSounds,
    }),
    [state.enableSoundEffects, state.enabledSounds, methods.setEnableSoundEffects, methods.setEnabledSounds]
  );

  const favoriteSecurities = useMemo<ContextType<typeof FavoriteSecuritiesContext>>(
    () => ({
      ...(pick(state, keys(DefaultFavoriteSecuritiesConfig)) as typeof DefaultFavoriteSecuritiesConfig),
      ...(pick(methods, keys(DefaultFavoriteSecuritiesMethods)) as typeof DefaultFavoriteSecuritiesMethods),
    }),
    [methods, state]
  );

  const favoriteStrategies = useMemo<ContextType<typeof FavoriteStrategiesContext>>(
    () => ({
      ...(pick(state, keys(DefaultFavoriteStrategiesConfig)) as typeof DefaultFavoriteStrategiesConfig),
      ...(pick(methods, keys(DefaultFavoriteStrategiesMethods)) as typeof DefaultFavoriteStrategiesMethods),
    }),
    [methods, state]
  );

  const favoriteAggregations = useMemo<ContextType<typeof FavoriteAggregationsContext>>(
    () => ({
      ...(pick(state, keys(DefaultFavoriteAggregationsConfig)) as typeof DefaultFavoriteAggregationsConfig),
      ...(pick(methods, keys(DefaultFavoriteAggregationsMethods)) as typeof DefaultFavoriteAggregationsMethods),
    }),
    [methods, state]
  );

  const defaultAggregation = useMemo(
    () => ({
      ...(pick(state, keys(DefaultAggregationConfig)) as typeof DefaultAggregationConfig),
      ...(pick(methods, keys(DefaultAggregationMethods)) as typeof DefaultAggregationMethods),
    }),
    [methods, state]
  );

  const favoriteSubAccounts = useMemo<ContextType<typeof FavoriteSubAccountsContext>>(
    () => ({
      ...(pick(state, keys(DefaultFavoriteSubAccountsConfigState)) as typeof DefaultFavoriteSubAccountsConfigState),
      ...(pick(methods, keys(DefaultFavoriteSubAccountsMethods)) as typeof DefaultFavoriteSubAccountsMethods),
    }),
    [methods, state]
  );

  const defaultMarketAccounts = useMemo<ContextType<typeof DefaultMarketAccountsContext>>(
    () => ({
      ...(pick(state, keys(DefaultMarketAccountsConfigState)) as typeof DefaultMarketAccountsConfigState),
      ...(pick(methods, keys(DefaultMarketAccountsMethods)) as typeof DefaultMarketAccountsMethods),
    }),
    [methods, state]
  );

  // Not a big fan of this
  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const getTabsState = useCallback(
    (key: string) => {
      const tabs = state.tabs;
      return tabs[key as keyof typeof tabs];
    },
    [state.tabs]
  );

  const tabsStorage = useMemo<ContextType<typeof TabsStorage>>(
    () => ({
      getState: getTabsState,
      setSelectedIndex: methods.setTabsSelectedIndex,
      setItems: methods.setTabsItems,
    }),
    [getTabsState, methods.setTabsItems, methods.setTabsSelectedIndex]
  );

  // This is the top level monitoring tabs.
  const monitoringTabsProps = useMemo<UseMonitoringTabsContextProps>(() => {
    const tabId = 'monitoring-blotters';
    const saveState = getTabsState(tabId) as TabsState<MonitoringTab>;
    return {
      initialTabs: saveState?.items ?? [],
      initialSelectedIndex: saveState?.selectedIndex ?? 0,
      saveTabs: newItems => {
        methods.setTabsItems(tabId, newItems);
      },
    };
  }, [getTabsState, methods]);
  const monitoringTabs = useMonitoringTabsContext(monitoringTabsProps);

  const analyticsTabsProps = useMemo(() => {
    const saveState = getTabsState(ANALYTICS_REPORTS_TABS_ID) as TabsState<
      IAnalyticsReportTab | IAnalyticsOrderDetailsTab
    >;
    return {
      initialTabs: saveState?.items ?? [],
      initialSelectedIndex: saveState?.selectedIndex ?? 0,
      saveTabs: newItems => {
        methods.setTabsItems(ANALYTICS_REPORTS_TABS_ID, newItems);
      },
    } satisfies UseAnalyticsTabsContextProps;
  }, [getTabsState, methods]);
  const analyticsTabs = useAnalyticsTabsContext(analyticsTabsProps);

  const orderPresets = useMemo<ContextType<typeof OrderPresetsContext>>(
    () => ({
      orderPresetsList: state.orderPresetsList,
      orderPresetsByID: new Map(state.orderPresetsList.map(p => [p.id, p])),
      createOrderPreset: (preset: IOrderPreset) => {
        methods.setOrderPresets([...state.orderPresetsList, preset].toSorted((a, b) => a.name.localeCompare(b.name)));
      },
      updateOrderPreset: (id: string, preset: Omit<IOrderPreset, 'id'>) => {
        methods.setOrderPresets(state.orderPresetsList.map(p => (p.id === id ? { id: p.id, ...preset } : p)));
      },
      deleteOrderPreset: (id: string) => {
        methods.deleteOrderPreset(id);
      },
    }),
    [state.orderPresetsList, methods]
  );

  const portfolioSettings = usePortfolioAppConfigSettings(state, methods);

  const contextGuide = useMemo<ContextType<typeof ContextGuideContext>>(
    () => ({
      contextGuide: state.contextGuide,
      setGuide: methods.setGuide,
      getGuide: (guideID: string) => get(state.contextGuide, guideID, false),
    }),
    [state, methods]
  );

  const layoutConfig: AppLayoutConfigContextProps = useMemo(() => {
    return {
      setSelectedLayoutId: methods.setSelectedLayoutId,
      selectedLayoutIds: state.selectedLayoutIds,
      setLayoutConfig: methods.setLayoutConfig,
      updateLayoutPanelParams: methods.updateLayoutPanelParams,
      resetLayoutConfig: methods.resetLayoutConfig,
      reImportTabs: methods.reImportTabs,
      setSpacesConfigs: methods.setSpacesConfigs,
      mergeSpacesSectionConfig: methods.mergeSpacesSectionConfig,
      spacesConfigs: state.spacesConfigs,
    };
  }, [
    state.selectedLayoutIds,
    state.spacesConfigs,
    methods.setLayoutConfig,
    methods.updateLayoutPanelParams,
    methods.resetLayoutConfig,
    methods.reImportTabs,
    methods.setSelectedLayoutId,
    methods.setSpacesConfigs,
    methods.mergeSpacesSectionConfig,
  ]);

  const spaceConfig: SpaceConfigContextProps = useMemo(() => {
    return {
      updateSpace: methods.updateSpace,
      removeSpace: methods.removeSpace,
      spaces: {
        trading: state.spacesConfigs.trading?.layouts ?? [],
        monitoring: state.spacesConfigs.monitoring?.layouts ?? [],
        intel: state.spacesConfigs.intel?.layouts ?? [],
        dealer: state.spacesConfigs.dealer?.layouts ?? [],
      },
      setSpaces: methods.setSpacesConfigs,
    };
  }, [methods.updateSpace, methods.removeSpace, state.spacesConfigs, methods.setSpacesConfigs]);

  const marketSelectorConfig: MarketSelectorConfigContextProps = useMemo(
    () => ({
      advancedMode: state.marketSelectorConfig.advancedMode,
      showDisabledItems: state.marketSelectorConfig.showDisabledItems,
      credentialsOnly: state.marketSelectorConfig.credentialsOnly,
      selectionListShowAll: state.marketSelectorConfig.selectionListShowAll,
      setAdvancedMode: methods.setMarketSelectorAdvancedMode,
      setShowDisabledItems: methods.setMarketSelectorShowDisabledItems,
      setCredentialsOnly: methods.setMarketSelectorCredentialsOnly,
      setSelectionListShowAll: methods.setSelectionListShowAll,
    }),
    [
      state.marketSelectorConfig.advancedMode,
      state.marketSelectorConfig.showDisabledItems,
      state.marketSelectorConfig.credentialsOnly,
      state.marketSelectorConfig.selectionListShowAll,
      methods.setMarketSelectorAdvancedMode,
      methods.setMarketSelectorShowDisabledItems,
      methods.setMarketSelectorCredentialsOnly,
      methods.setSelectionListShowAll,
    ]
  );

  if (
    recentSymbols == null ||
    display == null ||
    trading == null ||
    blotters == null ||
    orderEntry == null ||
    tabsStorage == null ||
    orderPresets == null ||
    portfolioSettings == null
  ) {
    return null;
  }

  return (
    <AppLayoutConfigContext.Provider value={layoutConfig}>
      <SpaceConfigContext.Provider value={spaceConfig}>
        <OrderEntryContext.Provider value={orderEntry}>
          <RecentSymbolsContext.Provider value={recentSymbols}>
            <DisplaySettingsContext.Provider value={display}>
              <TradingSettingsContext.Provider value={trading}>
                <SoundSettingsContext.Provider value={sounds}>
                  <BlottersContext.Provider value={blotters}>
                    <TabsStorage.Provider value={tabsStorage}>
                      <FavoriteSecuritiesContext.Provider value={favoriteSecurities}>
                        <FavoriteStrategiesContext.Provider value={favoriteStrategies}>
                          <FavoriteAggregationsContext.Provider value={favoriteAggregations}>
                            <DefaultAggregationContext.Provider value={defaultAggregation}>
                              <FavoriteSubAccountsContext.Provider value={favoriteSubAccounts}>
                                <DefaultMarketAccountsContext.Provider value={defaultMarketAccounts}>
                                  <MonitoringTabsContext.Provider value={monitoringTabs}>
                                    <AnalyticsTabsContext.Provider value={analyticsTabs}>
                                      <OrderPresetsContext.Provider value={orderPresets}>
                                        <PortfolioSettingsContext.Provider value={portfolioSettings}>
                                          <ContextGuideContext.Provider value={contextGuide}>
                                            <MarketSelectorConfigContext.Provider value={marketSelectorConfig}>
                                              {children}
                                            </MarketSelectorConfigContext.Provider>
                                          </ContextGuideContext.Provider>
                                        </PortfolioSettingsContext.Provider>
                                      </OrderPresetsContext.Provider>
                                    </AnalyticsTabsContext.Provider>
                                  </MonitoringTabsContext.Provider>
                                </DefaultMarketAccountsContext.Provider>
                              </FavoriteSubAccountsContext.Provider>
                            </DefaultAggregationContext.Provider>
                          </FavoriteAggregationsContext.Provider>
                        </FavoriteStrategiesContext.Provider>
                      </FavoriteSecuritiesContext.Provider>
                    </TabsStorage.Provider>
                  </BlottersContext.Provider>
                </SoundSettingsContext.Provider>
              </TradingSettingsContext.Provider>
            </DisplaySettingsContext.Provider>
          </RecentSymbolsContext.Provider>
        </OrderEntryContext.Provider>
      </SpaceConfigContext.Provider>
    </AppLayoutConfigContext.Provider>
  );
};
